import React, { useState, useEffect } from 'react';
import worksheetService from '../../services/worksheetService';

const WorksheetPrintModal = ({
  isOpen,
  onClose,
  initialTestIds = [],
  availableTemplates = []
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [layoutType, setLayoutType] = useState('PatientWise');
  const [paperSize, setPaperSize] = useState('A4');
  const [orientation, setOrientation] = useState('Portrait');
  const [includeHeader, setIncludeHeader] = useState(true);
  const [includeFooter, setIncludeFooter] = useState(true);
  const [includeBarcode, setIncludeBarcode] = useState(true);
  const [includeSignature, setIncludeSignature] = useState(true);

  const [previewData, setPreviewData] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);

  // Auto-select default template if available
  useEffect(() => {
    if (isOpen && availableTemplates.length > 0) {
      const defaultTpl = availableTemplates.find((t) => t.isDefault) || availableTemplates[0];
      if (defaultTpl) {
        setSelectedTemplateId(defaultTpl.templateId);
        setLayoutType(defaultTpl.layoutType || 'PatientWise');
        setPaperSize(defaultTpl.paperSize || 'A4');
        setOrientation(defaultTpl.orientation || 'Portrait');
      }
    }
  }, [isOpen, availableTemplates]);

  // Fetch live print preview whenever configuration changes
  useEffect(() => {
    if (!isOpen || initialTestIds.length === 0) return;

    let isMounted = true;
    const loadPreview = async () => {
      setIsLoadingPreview(true);
      try {
        const payload = {
          templateId: selectedTemplateId ? parseInt(selectedTemplateId, 10) : null,
          testIds: initialTestIds,
          paperSize,
          orientation,
          includeHeader,
          includeFooter,
          includeBarcode,
          includeSignature,
          layoutTypeOverride: layoutType
        };

        const res = await worksheetService.getPrintPreview(payload);
        if (isMounted && res.success && res.data) {
          setPreviewData(res.data);
        }
      } catch (err) {
        console.error('Failed to load print preview:', err);
      } finally {
        if (isMounted) setIsLoadingPreview(false);
      }
    };

    loadPreview();
    return () => {
      isMounted = false;
    };
  }, [
    isOpen,
    selectedTemplateId,
    layoutType,
    paperSize,
    orientation,
    includeHeader,
    includeFooter,
    includeBarcode,
    includeSignature,
    initialTestIds
  ]);

  if (!isOpen) return null;

  const handleTemplateSelect = (tplId) => {
    setSelectedTemplateId(tplId);
    const tpl = availableTemplates.find((t) => t.templateId === parseInt(tplId, 10));
    if (tpl) {
      setLayoutType(tpl.layoutType || 'PatientWise');
      setPaperSize(tpl.paperSize || 'A4');
      setOrientation(tpl.orientation || 'Portrait');
    }
  };

  const handleBrowserPrint = async () => {
    try {
      const payload = {
        templateId: selectedTemplateId ? parseInt(selectedTemplateId, 10) : null,
        testIds: initialTestIds,
        paperSize,
        orientation,
        includeHeader,
        includeFooter,
        includeBarcode,
        includeSignature,
        layoutTypeOverride: layoutType
      };

      const htmlContent = await worksheetService.getPrintHtml(payload);
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 500);
      }
    } catch (err) {
      console.error('Direct print failed:', err);
      window.print();
    }
  };

  const handleExportPdf = async () => {
    handleBrowserPrint();
  };

  return (
    <div className="ws-modal-overlay" onClick={onClose}>
      <div
        className="ws-modal-card"
        style={{ width: '1240px', maxWidth: '96vw', height: '92vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="ws-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
              🖨️ Laboratory Worksheet Print &amp; PDF Engine
            </span>
            <span className="ws-badge" style={{ background: '#e0f2fe', color: '#0369a1', fontWeight: 700 }}>
              {previewData?.totalRecords || initialTestIds.length} Specimen(s)
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Zoom Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#f1f5f9', padding: '2px 6px', border: '1px solid #cbd5e1', borderRadius: '6px' }}>
              <button
                type="button"
                className="ws-btn ws-btn-outline ws-btn-sm"
                onClick={() => setZoomLevel((z) => Math.max(z - 15, 50))}
                title="Zoom Out"
              >
                -
              </button>
              <span style={{ fontSize: '12px', fontWeight: 600, minWidth: '42px', textAlign: 'center' }}>
                {zoomLevel}%
              </span>
              <button
                type="button"
                className="ws-btn ws-btn-outline ws-btn-sm"
                onClick={() => setZoomLevel((z) => Math.min(z + 15, 160))}
                title="Zoom In"
              >
                +
              </button>
              <button
                type="button"
                className="ws-btn ws-btn-outline ws-btn-sm"
                onClick={() => setZoomLevel(100)}
                title="Reset Zoom"
              >
                100%
              </button>
            </div>

            <button
              type="button"
              className="ws-btn ws-btn-primary"
              onClick={handleBrowserPrint}
            >
              🖨️ Print Worksheet
            </button>
            <button
              type="button"
              className="ws-btn ws-btn-outline"
              onClick={handleExportPdf}
            >
              📄 Export PDF
            </button>
            <button
              type="button"
              className="ws-btn ws-btn-outline ws-btn-sm"
              onClick={onClose}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Configuration Bar */}
        <div
          style={{
            padding: '10px 20px',
            background: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
            fontSize: '13px'
          }}
        >
          {/* Template Master Select */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label style={{ fontWeight: 600, color: '#475569' }}>Template:</label>
            <select
              className="ws-filter-select"
              value={selectedTemplateId}
              onChange={(e) => handleTemplateSelect(e.target.value)}
            >
              <option value="">-- Custom / Direct Layout --</option>
              {availableTemplates.map((t) => (
                <option key={t.templateId} value={t.templateId}>
                  {t.templateName} ({t.layoutType}) {t.isDefault ? '⭐ [Default]' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* 14 Layout Formats */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label style={{ fontWeight: 600, color: '#475569' }}>Layout Format:</label>
            <select
              className="ws-filter-select"
              value={layoutType}
              onChange={(e) => setLayoutType(e.target.value)}
            >
              <option value="PatientWise">1. Patient-Wise (Patient → Tests → Params)</option>
              <option value="TestWise">2. Test-Wise (Matrix: Test → Patients Columns)</option>
              <option value="PatientPlusTest">3. Patient + Test Grid</option>
              <option value="PatientPlusParameters">4. Patient + Parameters Breakdown</option>
              <option value="TestPlusPatients">5. Test + Patients Breakdown</option>
              <option value="SampleWise">6. Sample-Wise Accession</option>
              <option value="AnalyzerWise">7. Analyzer / Instrument Bench Run</option>
              <option value="BatchWise">8. Batch-Wise Run Manifest</option>
              <option value="DepartmentWise">9. Department-Wise</option>
              <option value="SectionWise">10. Section-Wise (Hematology, Bio, etc.)</option>
              <option value="TechnicianWise">11. Technician-Wise Workbench</option>
              <option value="Compact">12. Compact High-Density (Small Lab / STAT)</option>
              <option value="Detailed">13. Detailed Full Reference &amp; Methods</option>
              <option value="Custom">14. Custom Formatted</option>
            </select>
          </div>

          {/* Paper Size */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label style={{ fontWeight: 600, color: '#475569' }}>Paper:</label>
            <select
              className="ws-filter-select"
              value={paperSize}
              onChange={(e) => setPaperSize(e.target.value)}
            >
              <option value="A4">A4 (210 × 297 mm)</option>
              <option value="A5">A5 (148 × 210 mm)</option>
              <option value="Letter">US Letter (8.5 × 11 in)</option>
            </select>
          </div>

          {/* Orientation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label style={{ fontWeight: 600, color: '#475569' }}>Orientation:</label>
            <select
              className="ws-filter-select"
              value={orientation}
              onChange={(e) => setOrientation(e.target.value)}
            >
              <option value="Portrait">Portrait</option>
              <option value="Landscape">Landscape</option>
            </select>
          </div>

          {/* Toggle Checkboxes */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={includeHeader}
                onChange={(e) => setIncludeHeader(e.target.checked)}
              />
              Header
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={includeFooter}
                onChange={(e) => setIncludeFooter(e.target.checked)}
              />
              Footer
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={includeBarcode}
                onChange={(e) => setIncludeBarcode(e.target.checked)}
              />
              Barcode
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={includeSignature}
                onChange={(e) => setIncludeSignature(e.target.checked)}
              />
              Signatures
            </label>
          </div>
        </div>

        {/* Live Sheet Viewport */}
        <div className="ws-modal-body" style={{ background: '#525659', padding: '24px' }}>
          {isLoadingPreview ? (
            <div style={{ padding: '80px 20px', textAlign: 'center', color: '#ffffff' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔄</div>
              <div>Rendering live laboratory worksheet preview...</div>
            </div>
          ) : !previewData ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#ffffff' }}>
              No print preview available.
            </div>
          ) : (
            <div
              style={{
                transform: `scale(${zoomLevel / 100})`,
                transformOrigin: 'top center',
                transition: 'transform 0.15s ease'
              }}
            >
              {/* Render Simulated Printable Sheet */}
              <div
                className={`ws-paper-sheet ${
                  orientation === 'Landscape' ? 'ws-paper-a4-landscape' : 'ws-paper-a4-portrait'
                }`}
              >
                {/* Printable Header */}
                {includeHeader && (
                  <div
                    style={{
                      borderBottom: '2px solid #000000',
                      paddingBottom: '12px',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div>
                      <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 900, textTransform: 'uppercase' }}>
                        {previewData.centreName || 'CENTRAL PATHOLOGY DIAGNOSTIC LABORATORY'}
                      </h2>
                      <div style={{ fontSize: '13px', fontWeight: 700, marginTop: '2px', color: '#1e293b' }}>
                        WORKSHEET / TECHNICIAN WORKBENCH — {previewData.template?.templateName || previewData.templateName || 'Standard Worksheet'}
                      </div>
                      <div style={{ fontSize: '11px', color: '#475569' }}>
                        Layout: <strong>{layoutType || previewData.template?.layoutType || 'PatientWise'}</strong> • Generated:{' '}
                        {new Date().toLocaleString()}
                      </div>
                    </div>
                    {includeBarcode && (
                      <div style={{ textAlign: 'right' }}>
                        <div
                          style={{
                            fontFamily: 'monospace',
                            fontWeight: 800,
                            letterSpacing: '3px',
                            fontSize: '15px',
                            border: '1px solid #000',
                            padding: '4px 8px'
                          }}
                        >
                          *WS-{Date.now().toString().slice(-6)}*
                        </div>
                        <div style={{ fontSize: '10px', color: '#64748b' }}>CONFIDENTIAL LAB RECORD</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Printable Body Content depending on Layout */}
                {renderPreviewLayout(previewData, layoutType)}

                {/* Printable Footer */}
                {includeFooter && (
                  <div
                    style={{
                      borderTop: '1px solid #000000',
                      paddingTop: '14px',
                      marginTop: '28px',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                      fontSize: '11px'
                    }}
                  >
                    <div>
                      <div>
                        Operator / Technician: <strong>{previewData.generatedBy || 'Technician'}</strong>
                      </div>
                      <div>Total Specimens: {previewData.totalSamples || previewData.totalRecords || previewData.samples?.length || 0}</div>
                    </div>
                    {includeSignature && (
                      <div style={{ display: 'flex', gap: '36px' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ width: '130px', borderBottom: '1px dotted #000', height: '24px' }} />
                          <div style={{ marginTop: '4px', fontWeight: 600 }}>Technician Sign</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ width: '130px', borderBottom: '1px dotted #000', height: '24px' }} />
                          <div style={{ marginTop: '4px', fontWeight: 600 }}>Pathologist / Supervisor</div>
                        </div>
                      </div>
                    )}
                    <div style={{ textAlign: 'right', color: '#64748b' }}>
                      Page 1 of 1
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Pure layout generator for simulated live print preview
 */
function renderPreviewLayout(previewData, layoutType) {
  let groups = previewData.groups || [];

  // Transform backend samples list into printable layout groups if groups are not pre-split
  if (groups.length === 0 && previewData.samples && previewData.samples.length > 0) {
    if (layoutType === 'TestWise' || layoutType === 'TestPlusPatients') {
      const byTest = {};
      previewData.samples.forEach((s) => {
        const key = s.testName || 'Laboratory Test';
        if (!byTest[key]) byTest[key] = [];
        if (s.observations && s.observations.length > 0) {
          s.observations.forEach((obs) => {
            byTest[key].push({
              sampleNo: s.sampleNo,
              labNo: s.labNo,
              patientName: s.patientName,
              age: s.ageYear,
              gender: s.gender,
              priority: s.priority,
              testName: s.testName,
              parameterName: obs.parameterName,
              currentValue: obs.currentValue,
              readingFormat: obs.readingFormat,
              minRange: obs.minRange,
              maxRange: obs.maxRange,
              displayReading: obs.displayReading,
              methodName: s.analyzerName || 'Benchtop'
            });
          });
        } else {
          byTest[key].push({
            sampleNo: s.sampleNo,
            labNo: s.labNo,
            patientName: s.patientName,
            age: s.ageYear,
            gender: s.gender,
            priority: s.priority,
            testName: s.testName,
            parameterName: s.testName,
            currentValue: '',
            readingFormat: '',
            displayReading: 'Normal',
            methodName: s.analyzerName || 'Benchtop'
          });
        }
      });
      groups = Object.keys(byTest).map((tName) => ({
        groupTitle: tName,
        rows: byTest[tName]
      }));
    } else {
      groups = previewData.samples.map((s) => {
        const rows = (s.observations && s.observations.length > 0)
          ? s.observations.map((obs) => ({
              sampleNo: s.sampleNo,
              labNo: s.labNo,
              sampleType: s.sampleType,
              patientName: s.patientName,
              age: s.ageYear,
              gender: s.gender,
              priority: s.priority,
              testName: s.testName,
              parameterName: obs.parameterName,
              currentValue: obs.currentValue,
              readingFormat: obs.readingFormat,
              minRange: obs.minRange,
              maxRange: obs.maxRange,
              displayReading: obs.displayReading,
              methodName: obs.methodName || s.analyzerName || 'Benchtop'
            }))
          : [{
              sampleNo: s.sampleNo,
              labNo: s.labNo,
              sampleType: s.sampleType,
              patientName: s.patientName,
              age: s.ageYear,
              gender: s.gender,
              priority: s.priority,
              testName: s.testName,
              parameterName: s.testName,
              currentValue: '',
              readingFormat: '',
              displayReading: 'Normal',
              methodName: s.analyzerName || 'Benchtop'
            }];

        return {
          groupTitle: `${s.patientName} (${s.ageGender || `${s.ageYear}/${s.gender || 'M'}`})`,
          rows
        };
      });
    }
  }

  if (groups.length === 0) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>No specimen rows in preview.</div>;
  }

  // 1. Test-Wise Matrix format (High-volume laboratory layout)
  if (layoutType === 'TestWise' || layoutType === 'TestPlusPatients') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {groups.map((group, gIdx) => (
          <div key={gIdx} style={{ border: '1px solid #000', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ background: '#f1f5f9', padding: '6px 10px', fontWeight: 800, fontSize: '13px', borderBottom: '1px solid #000' }}>
              TEST: {group.groupTitle} ({group.rows?.length || 0} Patients)
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11.5px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #000' }}>
                  <th style={{ borderRight: '1px solid #ccc', padding: '5px', textAlign: 'left', width: '30px' }}>#</th>
                  <th style={{ borderRight: '1px solid #ccc', padding: '5px', textAlign: 'left', width: '90px' }}>Sample No</th>
                  <th style={{ borderRight: '1px solid #ccc', padding: '5px', textAlign: 'left', width: '90px' }}>Lab No</th>
                  <th style={{ borderRight: '1px solid #ccc', padding: '5px', textAlign: 'left' }}>Patient Demographics</th>
                  <th style={{ borderRight: '1px solid #ccc', padding: '5px', textAlign: 'left', width: '50px' }}>Pri</th>
                  <th style={{ borderRight: '1px solid #ccc', padding: '5px', textAlign: 'left', width: '140px' }}>Parameter(s)</th>
                  <th style={{ padding: '5px', textAlign: 'left', width: '120px' }}>Result / Bench Entry</th>
                </tr>
              </thead>
              <tbody>
                {group.rows?.map((row, rIdx) => (
                  <tr key={rIdx} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ borderRight: '1px solid #ccc', padding: '5px' }}>{rIdx + 1}</td>
                    <td style={{ borderRight: '1px solid #ccc', padding: '5px', fontWeight: 700 }}>{row.sampleNo}</td>
                    <td style={{ borderRight: '1px solid #ccc', padding: '5px' }}>{row.labNo}</td>
                    <td style={{ borderRight: '1px solid #ccc', padding: '5px' }}>
                      <strong>{row.patientName}</strong> ({row.age}/{row.gender})
                    </td>
                    <td style={{ borderRight: '1px solid #ccc', padding: '5px', fontWeight: row.priority === 'STAT' ? 800 : 400 }}>
                      {row.priority}
                    </td>
                    <td style={{ borderRight: '1px solid #ccc', padding: '5px' }}>{row.parameterName || row.testName}</td>
                    <td style={{ padding: '5px', borderBottom: '1px dotted #999', background: '#fafafa' }}>
                      {row.currentValue || '________________'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  }

  // 2. Compact Layout format (High density)
  if (layoutType === 'Compact') {
    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
        <thead>
          <tr style={{ background: '#e2e8f0', borderBottom: '2px solid #000' }}>
            <th style={{ padding: '4px', textAlign: 'left' }}>Sample No</th>
            <th style={{ padding: '4px', textAlign: 'left' }}>Lab No</th>
            <th style={{ padding: '4px', textAlign: 'left' }}>Patient</th>
            <th style={{ padding: '4px', textAlign: 'left' }}>Test / Parameters</th>
            <th style={{ padding: '4px', textAlign: 'left' }}>Result</th>
            <th style={{ padding: '4px', textAlign: 'left' }}>Unit &amp; Normal Range</th>
            <th style={{ padding: '4px', textAlign: 'left' }}>Pri</th>
          </tr>
        </thead>
        <tbody>
          {groups.flatMap((g) => g.rows || []).map((row, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid #ccc' }}>
              <td style={{ padding: '4px', fontWeight: 700 }}>{row.sampleNo}</td>
              <td style={{ padding: '4px' }}>{row.labNo}</td>
              <td style={{ padding: '4px' }}>
                {row.patientName} ({row.age}/{row.gender})
              </td>
              <td style={{ padding: '4px', fontWeight: 600 }}>{row.testName} - {row.parameterName || ''}</td>
              <td style={{ padding: '4px', borderBottom: '1px dotted #888' }}>
                {row.currentValue || '_________'}
              </td>
              <td style={{ padding: '4px', fontSize: '10px' }}>
                {row.readingFormat} ({row.displayReading || 'Normal'})
              </td>
              <td style={{ padding: '4px', fontWeight: row.priority === 'STAT' ? 800 : 400 }}>
                {row.priority}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // 3. Default Patient-Wise / Patient+Parameters / Detailed layout
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
      {groups.map((group, gIdx) => (
        <div key={gIdx} style={{ border: '1px solid #cbd5e1', borderRadius: '4px', overflow: 'hidden' }}>
          {/* Patient Header Box */}
          <div
            style={{
              background: '#f8fafc',
              padding: '8px 12px',
              borderBottom: '1px solid #cbd5e1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <div>
              <span style={{ fontSize: '13px', fontWeight: 800 }}>{group.groupTitle}</span>
              <span style={{ fontSize: '11px', color: '#475569', marginLeft: '10px' }}>
                Lab No: <strong>{group.rows?.[0]?.labNo}</strong> • Sample:{' '}
                <strong>{group.rows?.[0]?.sampleNo}</strong> ({group.rows?.[0]?.sampleType || 'Blood'})
              </span>
            </div>
            <div style={{ fontSize: '11px', fontWeight: 700 }}>
              Priority: {group.rows?.[0]?.priority || 'Routine'}
            </div>
          </div>

          {/* Observations Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #cbd5e1' }}>
                <th style={{ padding: '6px 10px', textAlign: 'left', width: '22%' }}>Test / Parameter</th>
                <th style={{ padding: '6px 10px', textAlign: 'left', width: '18%' }}>Manual Result Entry</th>
                <th style={{ padding: '6px 10px', textAlign: 'left', width: '12%' }}>Unit</th>
                <th style={{ padding: '6px 10px', textAlign: 'left', width: '24%' }}>Biological Reference Interval</th>
                <th style={{ padding: '6px 10px', textAlign: 'left', width: '24%' }}>Method / Analyzer</th>
              </tr>
            </thead>
            <tbody>
              {group.rows?.map((row, rIdx) => (
                <tr key={rIdx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '6px 10px', fontWeight: 600 }}>
                    {row.parameterName || row.testName}
                  </td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px dotted #999', background: '#fafafa' }}>
                    {row.currentValue || ''}
                  </td>
                  <td style={{ padding: '6px 10px', color: '#475569' }}>{row.readingFormat || '-'}</td>
                  <td style={{ padding: '6px 10px', fontSize: '11px', color: '#475569' }}>
                    {row.minRange || row.maxRange ? `${row.minRange || 0} - ${row.maxRange || ''}` : row.displayReading || 'Normal'}
                  </td>
                  <td style={{ padding: '6px 10px', fontSize: '11px', color: '#64748b' }}>
                    {row.methodName || 'Benchtop'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

export default WorksheetPrintModal;
